import type { DirectorAddress } from '@/features/auth';

/**
 * ASIC returns one address per role a person holds, so a director who is also
 * the secretary of the same company appears with the same address two or three
 * times. The old portal rendered every copy; this collapses them.
 *
 * Comparison is on the formatted line rather than field-by-field, so two
 * records that differ only by `null` versus `''` in a field count as one
 * address — which is what a reader would call them.
 */
export const selectUniqueAddresses = (addresses: DirectorAddress[]): string[] => {
  const seen = new Set<string>();

  for (const address of addresses) {
    const formatted = formatAddress(address);
    if (formatted !== '') seen.add(formatted);
  }

  return [...seen];
};

/**
 * One address on one line.
 *
 * Parts are filtered before joining rather than interpolated, which is what
 * keeps a missing suite from leaving a leading comma. An address with nothing
 * in it formats to an empty string, and the caller decides what to show.
 */
export const formatAddress = (address: DirectorAddress): string => {
  const street = [address.suite, address.street]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join('/');

  return [street, address.suburb, address.state, address.postalCode, address.country]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
};
